import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence, useInView, useScroll, useTransform, useMotionValueEvent } from 'framer-motion';
import { Sparkles } from 'lucide-react';

const EASE = [0.16, 1, 0.3, 1];

const rgba = (hex, a) => {
  const m = hex.replace('#', '');
  const r = parseInt(m.substring(0, 2), 16);
  const g = parseInt(m.substring(2, 4), 16);
  const b = parseInt(m.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
};

/* FlexiOss is the top card (revealed first) → Allograft → Autograft. */
const OPTIONS = [
  {
    key: 'flexioss',
    name: 'FlexiOss® Biphasic Matrix',
    type: 'Synthetic · Out-of-pack',
    tone: 'flexioss',
    verdict: 'One site. Predictable cost. The modern standard of care.',
    params: [
      { label: 'Surgical Sites Required', value: 'One — primary defect only.', good: true },
      { label: 'Operating-Room Time', value: 'Minimized — ready out-of-pack in under 60 seconds.', good: true },
      { label: 'Donor-Site Morbidity', value: 'Zero — eliminates secondary harvest trauma.', good: true },
      { label: 'Antibiotic Carrying', value: 'Optimal — the porous curdlan matrix carries targeted antibiotics as a stable, localized delivery system.', good: true },
      { label: 'Total Institutional Cost', value: 'Predictable — optimized clinical lifecycle efficiency.', good: true },
    ],
  },
  {
    key: 'allograft',
    name: 'Traditional Allograft',
    type: 'Donor-Sourced',
    tone: 'neutral',
    verdict: 'A single site — but variable sourcing and weak therapeutics.',
    params: [
      { label: 'Surgical Sites Required', value: 'One — primary defect.' },
      { label: 'Operating-Room Time', value: 'Standard preparation time.' },
      { label: 'Donor-Site Morbidity', value: 'Not applicable.' },
      { label: 'Antibiotic Carrying', value: 'Poor chemical impregnation.' },
      { label: 'Total Institutional Cost', value: 'High — variable importation and sourcing costs.' },
    ],
  },
  {
    key: 'autograft',
    name: 'Autograft',
    type: 'Patient Harvest',
    tone: 'caution',
    verdict: 'Two surgical sites and the highest patient burden.',
    params: [
      { label: 'Surgical Sites Required', value: 'Two — primary defect plus an iliac-crest donor site.' },
      { label: 'Operating-Room Time', value: 'Extended by 30–45 minutes for harvest.' },
      { label: 'Donor-Site Morbidity', value: 'High clinical risk; prolonged recovery points.' },
      { label: 'Antibiotic Carrying', value: 'Unstable; carries structural degradation risk.' },
      { label: 'Total Institutional Cost', value: 'High — extended OR usage and recovery management.' },
    ],
  },
];

const TONES = {
  flexioss: { accent: '#3b82f6', badge: 'RECOMMENDED', border: 'border-blue-400/30', grad: 'from-[#0a1f3c] via-slate-900 to-slate-950', ring: 'shadow-[0_50px_140px_-30px_rgba(59,130,246,0.5)]' },
  neutral: { accent: '#94a3b8', badge: null, border: 'border-white/12', grad: 'from-slate-900 via-slate-900 to-slate-950', ring: 'shadow-[0_50px_140px_-40px_rgba(0,0,0,0.9)]' },
  caution: { accent: '#E87A20', badge: null, border: 'border-[#E87A20]/25', grad: 'from-[#241606] via-slate-900 to-slate-950', ring: 'shadow-[0_50px_140px_-40px_rgba(0,0,0,0.9)]' },
};

/* ---- The visual card (shared between desktop deck + mobile stack) -------- */
const DeckCard = ({ option, index }) => {
  const t = TONES[option.tone];
  return (
    <div
      data-testid={`coc-deck-${option.key}`}
      className={`relative flex h-auto w-full flex-col overflow-hidden border ${t.border} bg-gradient-to-br ${t.grad} p-7 backdrop-blur-xl ${t.ring} md:h-[540px] md:p-12`}
    >
      <span aria-hidden="true" className="absolute left-0 top-0 h-[3px] w-full" style={{ background: `linear-gradient(to right, ${t.accent}, transparent)` }} />
      <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full blur-3xl" style={{ background: rgba(t.accent, 0.16) }} />

      <div className="relative flex items-start justify-between gap-4">
        <div>
          <span className="font-mono text-xs tracking-[0.32em] text-slate-500">{String(index + 1).padStart(2, '0')} / 03</span>
          <h3 className="mt-2 font-serif text-3xl italic leading-tight text-white md:text-[2.6rem]">{option.name}</h3>
          <p className="mt-1.5 text-[11px] font-bold uppercase tracking-[0.2em] md:text-xs" style={{ color: t.accent }}>{option.type}</p>
        </div>
        {t.badge && (
          <span className="flex flex-shrink-0 items-center gap-1.5 border border-blue-400/40 bg-blue-500/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-blue-300 md:text-[11px]">
            <Sparkles className="h-3 w-3" strokeWidth={2} /> {t.badge}
          </span>
        )}
      </div>

      <div className="relative mt-7 flex-1 border-t border-white/10">
        {option.params.map((p, i) => (
          <div key={i} className="grid grid-cols-1 gap-1 border-b border-white/[0.06] py-3.5 md:grid-cols-[230px_1fr] md:gap-8 md:py-4">
            <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 md:text-xs">{p.label}</span>
            <span className={`text-sm leading-snug md:text-[15px] ${p.good ? 'text-white' : 'text-slate-300'}`}>
              {p.good && <span aria-hidden="true" className="mr-2 inline-block h-1.5 w-1.5 align-middle" style={{ background: t.accent }} />}
              {p.value}
            </span>
          </div>
        ))}
      </div>

      <p className="relative mt-6 font-serif text-lg italic md:text-xl" style={{ color: t.accent }}>{option.verdict}</p>
    </div>
  );
};

/* ---- One animated card in the scroll-driven deck ------------------------ */
const StackCard = ({ progress, enter, exit, z, children }) => {
  const stops = [];
  const yOut = [], scaleOut = [], opOut = [], rotOut = [];
  if (enter) { stops.push(enter[0], enter[1]); yOut.push(48, 0); scaleOut.push(0.9, 1); opOut.push(1, 1); rotOut.push(0, 0); }
  else { stops.push(0); yOut.push(0); scaleOut.push(1); opOut.push(1); rotOut.push(0); }
  if (exit) { stops.push(exit[0], exit[1]); yOut.push(0, -820); scaleOut.push(1, 1.06); opOut.push(1, 0); rotOut.push(0, -7); }
  else { stops.push(1); yOut.push(0); scaleOut.push(1); opOut.push(1); rotOut.push(0); }

  const y = useTransform(progress, stops, yOut);
  const scale = useTransform(progress, stops, scaleOut);
  const opacity = useTransform(progress, stops, opOut);
  const rotate = useTransform(progress, stops, rotOut);

  return (
    <motion.div
      style={{ y, scale, opacity, rotate, zIndex: z }}
      className="absolute inset-x-0 top-0 origin-center will-change-transform"
    >
      {children}
    </motion.div>
  );
};

const RANGES = [
  { enter: null, exit: [0.05, 0.33] },
  { enter: [0.08, 0.33], exit: [0.42, 0.70] },
  { enter: [0.48, 0.90], exit: null },
];

const StackedDeck = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });
  const [active, setActive] = useState(0);
  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    setActive(v < 0.36 ? 0 : v < 0.71 ? 1 : 2);
  });
  const activeAccent = TONES[OPTIONS[active].tone].accent;

  return (
    <div ref={ref} data-testid="coc-deck" className="relative h-[230vh]">
      <div className="sticky top-0 flex h-screen items-center justify-center px-6 lg:px-8">
        {/* Progress indicator — 01/03 + dots */}
        <div data-testid="coc-progress" className="pointer-events-none absolute left-6 top-1/2 hidden -translate-y-1/2 flex-col items-start gap-5 lg:flex xl:left-12">
          <span data-testid="coc-progress-counter" className="font-mono text-sm tracking-[0.3em] text-slate-300">
            {String(active + 1).padStart(2, '0')}<span className="text-slate-600"> / 03</span>
          </span>
          <div className="flex flex-col gap-3">
            {OPTIONS.map((o, i) => (
              <span
                key={o.key}
                className="w-[3px] rounded-full transition-all duration-500"
                style={{
                  height: i === active ? 44 : 16,
                  background: i === active ? activeAccent : 'rgba(255,255,255,0.15)',
                  boxShadow: i === active ? `0 0 14px ${rgba(activeAccent, 0.6)}` : 'none',
                }}
              />
            ))}
          </div>
        </div>

        {/* The stacked deck */}
        <div className="relative w-full max-w-4xl" style={{ height: 540 }}>
          {OPTIONS.map((o, i) => (
            <StackCard key={o.key} progress={scrollYProgress} enter={RANGES[i].enter} exit={RANGES[i].exit} z={30 - i * 10}>
              <DeckCard option={o} index={i} />
            </StackCard>
          ))}
        </div>
      </div>
    </div>
  );
};

const swipeVariants = {
  enter: (d) => ({ opacity: 0, x: d > 0 ? 90 : -90, scale: 0.96 }),
  center: { opacity: 1, x: 0, scale: 1 },
  exit: (d) => ({ opacity: 0, x: d > 0 ? -90 : 90, scale: 0.96 }),
};

/* Mobile / tablet — a lighter, swipeable animated card stack (choice 3b). */
const MobileSwipeStack = () => {
  const [[index, dir], setState] = useState([0, 0]);
  const go = (next) => {
    const clamped = Math.min(OPTIONS.length - 1, Math.max(0, next));
    if (clamped === index) return;
    setState([clamped, clamped > index ? 1 : -1]);
  };
  const accent = TONES[OPTIONS[index].tone].accent;

  return (
    <div data-testid="coc-mobile-swipe" className="mx-auto mt-6 max-w-2xl px-4 pb-2 md:hidden">
      {/* Counter + progress dots */}
      <div className="mb-5 flex items-center justify-between">
        <span data-testid="coc-progress-counter" className="font-mono text-xs tracking-[0.3em] text-slate-400">
          {String(index + 1).padStart(2, '0')}<span className="text-slate-600"> / 03</span>
        </span>
        <div className="flex items-center gap-2">
          {OPTIONS.map((o, i) => (
            <button
              key={o.key}
              data-testid={`coc-swipe-dot-${i}`}
              aria-label={`Show ${o.name}`}
              onClick={() => go(i)}
              className="h-1.5 rounded-full transition-all duration-500"
              style={{ width: i === index ? 28 : 8, background: i === index ? accent : 'rgba(148,163,184,0.35)' }}
            />
          ))}
        </div>
      </div>

      {/* Swipe viewport */}
      <div className="relative overflow-hidden">
        <AnimatePresence initial={false} custom={dir} mode="wait">
          <motion.div
            key={OPTIONS[index].key}
            custom={dir}
            variants={swipeVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.42, ease: EASE }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={(e, info) => {
              if (info.offset.x < -55 || info.velocity.x < -350) go(index + 1);
              else if (info.offset.x > 55 || info.velocity.x > 350) go(index - 1);
            }}
            className="cursor-grab touch-pan-y active:cursor-grabbing"
            data-testid={`coc-card-${index}`}
          >
            <DeckCard option={OPTIONS[index]} index={index} />
          </motion.div>
        </AnimatePresence>
      </div>

      <p className="mt-5 text-center font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">
        Swipe or tap the dots to compare
      </p>
    </div>
  );
};

const CostOfCareMatrix = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    // NOTE: no overflow-hidden on this wrapper — it would break the deck's position:sticky.
    <div ref={ref} data-testid="cost-of-care-matrix" className="relative bg-slate-950">
      {/* Header (clips its own ambient glow locally so sticky stays intact) */}
      <div className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-24 right-0 h-72 w-72 rounded-full bg-blue-600/10 blur-[120px]" />
        <div className="relative mx-auto max-w-6xl px-4 pt-20 md:px-12 md:pt-28 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.75, ease: EASE }}
            className="max-w-3xl"
          >
            <p className="font-display text-[11px] font-bold uppercase tracking-[0.32em] text-[#E87A20]">
              Total Cost-of-Care Analysis
            </p>
            <h2 className="mt-4 font-serif text-3xl italic leading-[1.08] tracking-tight text-white md:text-5xl lg:text-[3.5rem]">
              Why is FlexiOss<sup className="align-super text-xl not-italic">®</sup> cheaper in the long run?
            </h2>
            <p className="mt-6 text-base leading-relaxed text-slate-300 md:text-lg">
              Autografts and donor allografts hide their true cost — a second surgical site, longer operating-room time,
              and unpredictable resorption. FlexiOss<sup>®</sup> removes the harvest site entirely and accelerates structural
              remodeling by 50%, shortening hospital stays and lowering post-operative revision liability.
            </p>
            {isDesktop && (
              <p className="mt-7 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.28em] text-slate-500">
                <span className="h-px w-8 bg-slate-600" /> Scroll to compare the three options
              </p>
            )}
          </motion.div>
        </div>
      </div>

      {/* Stacked deck (desktop) / swipeable animated stack (mobile) */}
      {isDesktop ? <StackedDeck /> : <MobileSwipeStack />}

      {/* Targeted Antibiotic Carrier — laboratory-extract module */}
      <div className="relative mx-auto max-w-6xl px-4 pb-20 pt-6 md:px-12 md:pb-28 lg:px-24">
        <div
          data-testid="coc-carrier-callout"
          className="group relative ml-auto w-full max-w-3xl overflow-hidden border-[1px] border-white/10 bg-white/[0.01] p-6 backdrop-blur-md transition-all duration-[600ms] ease-[cubic-bezier(0.16,1,0.3,1)] hover:border-orange-500/30 md:p-10 lg:mr-[8%]"
        >
          <p className="font-sans text-xs uppercase tracking-widest text-white/50">[ CLINICAL EVALUATION // MATRIX_DATA_04 ]</p>
          <h3 className="mb-4 mt-2 font-serif text-xl italic tracking-tight text-white md:text-2xl lg:text-3xl">
            Microstructural Stability as a Targeted Antibiotic Carrier
          </h3>
          <p className="max-w-3xl font-sans text-sm leading-relaxed text-white/80 md:text-base">
            Unlike legacy ceramics that fragment or react violently when impregnated with local therapeutics, the patented curdlan polymer triple-helix within FlexiOss® allows secure loading of targeted antibiotics directly into its porous microstructure, serving as an intraoperative localized drug delivery system without structural degradation or chemical resistance.
          </p>
          <p className="mt-6 border-t border-white/5 pt-4 font-sans text-xs tracking-wide text-white/40">
            Verification Index: Belcarz A., Zima A., Ginalska G. “Biphasic mode of antibacterial action of aminoglycoside antibiotics-loaded elastic hydroxyapatite-glucan composite.” International Journal of Pharmaceutics, 2013.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CostOfCareMatrix;
